/**
 * Ref resolution utilities for handling different types of React refs
 * Supports DOM elements, React components with forwardRef, and custom component structures
 */

/**
 * Attempts to find a DOM element within a React component instance
 * Traverses common patterns used by UI libraries
 *
 * @param {Object} component - React component instance
 * @returns {HTMLElement|null} Found DOM element or null
 */
const findDOMElementInComponent = (component) => {
  // Common patterns for finding DOM elements in components:

  // 1. Direct DOM element (already checked above, but included for completeness)
  if (component instanceof HTMLElement) {
    return component;
  }

  // 2. Component has a 'ref' or 'elementRef' property
  if (component.ref?.current instanceof HTMLElement) {
    return component.ref.current;
  }

  if (component.elementRef?.current instanceof HTMLElement) {
    return component.elementRef.current;
  }

  // 3. Component has a 'current' property that's a DOM element
  if (component.current instanceof HTMLElement) {
    return component.current;
  }

  // 4. Check if component is a wrapper with children
  // Some components might have their DOM element as the first child
  const { children } = component;
  if (children && children.length > 0) {
    let i = 0;
    const len = children.length;
    for (; i < len; i += 1) {
      const found = findDOMElementInComponent(children[i]);
      if (found) return found;
    }
  }

  // 5. Check component props for ref patterns
  if (component.props) {
    // Some libraries pass refs through props
    if (component.props.elementRef?.current instanceof HTMLElement) {
      return component.props.elementRef.current;
    }

    if (component.props.innerRef?.current instanceof HTMLElement) {
      return component.props.innerRef.current;
    }
  }

  // 6. Last resort: check if component has any HTMLElement properties
  // This handles cases where the component instance has direct DOM element properties
  const keys = Object.keys(component);
  const len = keys.length;
  let i = 0;
  for (; i < len; i += 1) {
    const key = keys[i];
    const value = component[key];
    if (value instanceof HTMLElement) {
      return value;
    }
  }

  return null;
};

/**
 * Resolves a ref to its DOM element
 * Handles various ref types: DOM elements, React components, forwardRef components
 *
 * @param {Object} ref - React ref object
 * @returns {HTMLElement|null} DOM element or null if not found
 */
export const resolveRefToElement = (ref) => {
  if (!ref) return null;

  // Handle ref objects (useRef, createRef)
  const { current } = ref;
  if (!current) return null;

  // Direct DOM element
  if (current instanceof HTMLElement) {
    return current;
  }

  // React component instance (forwardRef)
  if (current && typeof current === 'object') {
    // Check for common DOM element properties
    if (current.nodeType === 1) {
      return current;
    }

    // Check if it's a React component with a DOM ref
    // Some components might expose their DOM element via a specific property
    if (current.elementRef?.current instanceof HTMLElement) {
      return current.elementRef.current;
    }

    // Try to find a DOM element by traversing common patterns
    // This handles cases where components wrap their DOM element
    const domElement = findDOMElementInComponent(current);
    if (domElement) {
      return domElement;
    }
  }

  return null;
};


/**
 * Validates that a ref can be resolved to a DOM element
 * Useful for early validation in development
 *
 * @param {Object} ref - React ref object
 * @returns {boolean} True if ref can be resolved to DOM element
 */
export const isValidRef = (ref) => {
  return resolveRefToElement(ref) !== null;
};

/**
 * Gets bounding rectangle for a ref, with automatic resolution
 * Combines ref resolution with zoom correction
 *
 * @param {Object} ref - React ref object
 * @param {Function} getZoomCorrectedRect - Zoom correction function
 * @returns {Object|null} Zoom-corrected bounding rectangle or null
 */
export const getResolvedRect = (ref, getZoomCorrectedRect) => {
  const element = resolveRefToElement(ref);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return getZoomCorrectedRect ? getZoomCorrectedRect(rect) : rect;
};

/**
 * Hook for resolved ref handling with automatic updates
 * Provides a stable interface for components that need DOM element access
 *
 * @param {Object} ref - React ref object
 * @returns {Object} Resolved element and utilities
 */
export const useResolvedRef = (ref) => {
  const element = resolveRefToElement(ref);

  return {
    element,
    isValid: element !== null,
    rect: element ? element.getBoundingClientRect() : null,
  };
};
